import {
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon
} from '@heroicons/react/24/solid'
import React, { useEffect, useRef, useState, memo } from 'react'
import { useHistory } from 'react-router-dom'
import i18n from '../i18n'

/**
 * Componente genérico para buscador y botón de crear
 * @param {Object} props
 * @param {string} props.filterText - Texto del filtro actual
 * @param {Function} props.setFilterText - Función para actualizar el filtro
 * @param {string} props.createRoute - Ruta para crear nuevo elemento (ej: '/campaign' o '/createCampaign')
 * @param {string} props.searchPlaceholder - Placeholder para el input de búsqueda
 * @param {string} props.createButtonTitle - Título del botón crear (para tooltip)
 * @param {boolean} [props.searchExpanded] - Estado externo del buscador expandido (opcional)
 * @param {Function} [props.setSearchExpanded] - Función para actualizar el estado externo (opcional)
 */
const SearchAndCreateButton = ({
  filterText,
  setFilterText,
  createRoute,
  searchPlaceholder,
  createButtonTitle,
  searchExpanded: externalSearchExpanded,
  setSearchExpanded: externalSetSearchExpanded
}) => {
  const history = useHistory()
  // Usar estado externo si se proporciona, sino usar estado interno
  const [internalSearchExpanded, setInternalSearchExpanded] = useState(false)
  const [searchClosing, setSearchClosing] = useState(false)
  const searchRef = useRef(null)
  
  const searchExpanded = externalSearchExpanded !== undefined ? externalSearchExpanded : internalSearchExpanded
  const setSearchExpanded = externalSetSearchExpanded || setInternalSearchExpanded

  // Mantener el buscador expandido si hay filtro activo
  useEffect(() => {
    const hasActiveFilter = filterText && filterText.trim()
    if (hasActiveFilter && !searchExpanded) {
      // Si hay filtro activo pero el buscador no está expandido, expandirlo
      setSearchExpanded(true)
      setSearchClosing(false)
    }
  }, [filterText, searchExpanded])

  // Hook para detectar clicks fuera del buscador
  useEffect(() => {
    const handleClickOutside = (event) => {
      // NO cerrar si hay filtro activo - mantener el buscador abierto
      const hasActiveFilter = filterText && filterText.trim()
      if (hasActiveFilter) {
        return // No hacer nada si hay filtro activo
      }

      if (
        searchExpanded &&
        !searchClosing &&
        searchRef.current &&
        !searchRef.current.contains(event.target)
      ) {
        // Si el click fue fuera del buscador y está expandido (y no hay filtro), cerrarlo con animación
        setSearchClosing(true)
        // Esperar a que termine la animación antes de cerrar (350ms = duración de la animación)
        setTimeout(() => {
          setSearchExpanded(false)
          setSearchClosing(false)
          // Limpiar el filtro si está vacío
          if (!filterText || !filterText.trim()) {
            setFilterText('')
          }
        }, 350) // Duración de la animación de cierre (0.35s)
      }
    }

    const hasActiveFilter = filterText && filterText.trim()
    // Solo agregar listener si está expandido, no está cerrando, y NO hay filtro activo
    if (searchExpanded && !searchClosing && !hasActiveFilter) {
      // Agregar listener cuando el buscador está expandido y no está cerrando
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      // Limpiar listener cuando el componente se desmonte o el buscador se cierre
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [searchExpanded, searchClosing, filterText, setFilterText])

  return (
    <div className="fixed bottom-6 z-50 left-[calc(50%+128px)] transform -translate-x-1/2 md:left-[calc(50%+128px)]">
      {/* Contenedor con efecto liquid glass más agresivo */}
      <div className="liquid-glass-menu flex items-center gap-3 px-5 py-3.5 rounded-2xl bg-white/85 backdrop-blur-2xl border-2 border-white/40 shadow-2xl ring-2 ring-white/30">
        {/* Buscador con animación */}
        <div className="relative" ref={searchRef}>
          {!searchExpanded ? (
            <button
              type="button"
              onClick={() => {
                setSearchExpanded(true)
                setSearchClosing(false)
              }}
              className="search-button flex items-center justify-center w-12 h-12 rounded-full bg-white/95 backdrop-blur-xl border-2 border-white/50 hover:bg-white transition-all duration-300 hover:scale-110 active:scale-95 shadow-2xl hover:shadow-2xl ring-2 ring-white/40"
            >
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-800" />
            </button>
          ) : (
            <div
              className={`search-expanded flex items-center rounded-full bg-white/95 backdrop-blur-xl border-2 border-white/50 shadow-2xl px-5 py-3 min-w-[320px] ring-2 ring-white/40 ${
                searchClosing ? 'search-collapsing' : ''
              }`}
            >
              <div className="flex items-center gap-3 flex-1">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-700 flex-shrink-0" />
                <input
                  type="text"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  autoFocus
                  placeholder={searchPlaceholder}
                  className="flex-1 bg-transparent border-none outline-none text-gray-900 placeholder-gray-500 text-sm font-semibold"
                />
                {filterText && (
                  <button
                    type="button"
                    onClick={() => {
                      setFilterText('')
                    }}
                    className="flex-shrink-0 text-gray-600 hover:text-gray-800 transition-colors p-1.5 rounded-full hover:bg-white/60 backdrop-blur-sm"
                    title="Limpiar búsqueda"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Botón circular Create */}
        <button
          type="button"
          onClick={() => history.push(createRoute)}
          className="create-campaign-button flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white shadow-md hover:shadow-lg transition-all duration-300 hover:scale-110 active:scale-95"
          title={createButtonTitle}
        >
          <PlusIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  )
}

// Memoizar el componente para evitar re-renderizados innecesarios
export default memo(SearchAndCreateButton)
